import { computed, Injectable, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { PermissionsService } from './core/permissions.service';
import { UsuarioService } from './usuario.service';
import { MenuItem } from '../types/menu-item';

export interface NavigationItem extends MenuItem {
  id: string;
  url: string;
  icon: string;
  adminOnly?: boolean;
  driverAllowed?: boolean;
}
export interface NavigationGroup {
  id: string;
  titulo: string;
  icon: string;
  submenu: NavigationItem[];
}

// Canonical destinations, shared by both layouts. Stored menus never grant access.
const UCO: NavigationItem[] = [
  { id: 'uco-clients', titulo: 'Prospects & Clients', url: '/uco/business/megamenu2', icon: 'clients', module: 'customers' },
  { id: 'uco-orders', titulo: 'Orders', url: '/uco/manage-order', icon: 'orders', module: 'orders' },
  { id: 'uco-collection', titulo: 'Collection', url: '/uco/uco', icon: 'collection', module: 'oilRecovery', driverAllowed: true },
  { id: 'uco-commissions', titulo: 'Commissions', url: '/uco/liquidations', icon: 'commissions', module: 'liquidations' },
];
const ECO: NavigationItem[] = [
  { id: 'eco-clients', titulo: 'Clients', url: '/eco/clients', icon: 'clients', module: 'customers', driverAllowed: true },
  { id: 'eco-orders', titulo: 'Orders', url: '/eco/orders', icon: 'orders', module: 'orders', driverAllowed: true },
  { id: 'eco-routes', titulo: 'Routes', url: '/eco/routes', icon: 'routes', module: 'ecoRoutes', driverAllowed: false },
];
const SETTINGS: NavigationItem[] = [
  { id: 'users', titulo: 'User Management', url: '/uco/user-management', icon: 'clients', module: 'users', adminOnly: true },
  { id: 'notifications', titulo: 'Notification Settings', url: '/management/notification-settings', icon: 'notifications', adminOnly: true },
  { id: 'integrations', titulo: 'Integrations', url: '/uco/integrations', icon: 'integrations', adminOnly: true },
  { id: 'signatures', titulo: 'Digital Signatures', url: '/uco/sa-management', icon: 'signatures', adminOnly: true },
  { id: 'selects', titulo: 'Selects Data', url: '/uco/selects', icon: 'selects', adminOnly: true },
  { id: 'eco-import', titulo: 'ECO Client Import', url: '/management/eco-client-import', icon: 'eco-import', adminOnly: true },
  { id: 'data-maintenance', titulo: 'Data Maintenance', url: '/management/data-maintenance', icon: 'data-maintenance', adminOnly: true },
  { id: 'eco-settings', titulo: 'ECO Settings', url: '/management/eco-settings', icon: 'eco-settings', adminOnly: true },
];
const ADMIN_ROLES = ['IT_ROLE', 'ADMIN_ROLE', 'SUPER_ADMIN_ROLE', 'SUPER_ROLE'];

@Injectable({ providedIn: 'root' })
export class SidebarService {
  private readonly storedMenu = signal<string | null>(null);
  private readonly collapsed = signal(false);
  readonly mobileOpen = signal(false);
  readonly expanded = signal<Record<string, boolean>>({ uco: true, eco: true, settings: false });
  readonly currentUrl = signal('');
  private sessionUser: unknown;
  private opener: HTMLElement | null = null;

  readonly groups = computed(() => {
    const user = this.usuarioService.usuario;
    if (!user) return [];
    const urls = this.storedUrls();
    const permitted = (item: NavigationItem) => {
      if (item.adminOnly && !ADMIN_ROLES.includes(user.role)) return false;
      if (user.role === 'DRIVER_ROLE' && !item.driverAllowed) return false;
      // Preserve the UCO technician restriction; ECO keeps its existing permission policy.
      if (user.role === 'SERVICES_ROLE' && !item.url.startsWith('/eco/') && item.url !== '/uco/manage-order') return false;
      return !item.module || this.permissions.hasModuleAccess(item.module);
    };
    const uco = UCO.filter(item => (!urls || urls.has(item.url)) && permitted(item));
    const settings = SETTINGS.filter(item => (!urls || urls.has(item.url) || item.id === 'data-maintenance' || item.id === 'eco-import' || (item.id === 'eco-settings' && ['/management/fleet-trucks', '/management/eco-operating-policies', '/management/eco-service-groups'].some(url => urls?.has(url)))) && permitted(item));
    return [
      { id: 'uco', titulo: 'UCO', icon: 'uco', submenu: uco },
      { id: 'eco', titulo: 'ECO', icon: 'eco', submenu: ECO.filter(permitted) },
      { id: 'settings', titulo: 'Settings', icon: 'settings', submenu: settings },
    ].filter(group => group.submenu.length > 0);
  });
  get menu(): NavigationGroup[] { return this.groups(); }
  get miniSidebar(): boolean { return this.collapsed(); }
  set miniSidebar(value: boolean) { this.collapsed.set(value); }
  get showHome(): boolean {
    const user = this.usuarioService.usuario;
    if (!user || user.role === 'DRIVER_ROLE' || user.role === 'SERVICES_ROLE') return false;
    const urls = this.storedUrls();
    return !urls || urls.has('/uco/dashboard') || urls.has('/') || urls.has('/dashboard');
  }
  get showDashboard(): boolean { return this.showHome; }
  get showReports(): boolean {
    const user = this.usuarioService.usuario;
    if (!user || user.role === 'DRIVER_ROLE' || user.role === 'SERVICES_ROLE') return false;
    if (!this.permissions.hasModuleAccess('reports')) return false;
    const urls = this.storedUrls();
    return !urls || urls.has('/uco/reports');
  }

  constructor(private permissions: PermissionsService, private usuarioService: UsuarioService, private router: Router) {
    this.cargarMenu();
    this.currentUrl.set(router.url);
    router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(event => {
      this.currentUrl.set((event as NavigationEnd).urlAfterRedirects);
      this.cargarMenu();
      this.revealActiveGroup();
      this.closeMobile();
    });
  }

  private storedUrls(): Set<string> | null {
    try {
      const raw = JSON.parse(this.storedMenu() || 'null');
      if (!Array.isArray(raw) || !raw.length) return null;
      const urls = new Set<string>();
      const visit = (items: unknown[], depth = 0) => {
        if (depth > 5) return;
        for (const item of items) {
          if (!item || typeof item !== 'object') continue;
          const node = item as MenuItem;
          if (typeof node.url === 'string') urls.add('/' + node.url.replace(/^\/+/, ''));
          if (Array.isArray(node.submenu)) visit(node.submenu, depth + 1);
        }
      };
      visit(raw);
      // Unknown or malformed payloads fall back to known routes, still permission-filtered.
      return (urls.has('/uco/dashboard') || urls.has('/uco/reports') || urls.has('/') || UCO.some(item => urls.has(item.url))) ? urls : null;
    } catch { return null; }
  }

  cargarMenu(): void {
    const uid = this.usuarioService.usuario?.uid ?? this.usuarioService.usuario;
    if (uid !== this.sessionUser) {
      this.sessionUser = uid;
      this.expanded.set({ eco: true, uco: true, settings: false });
      this.collapsed.set(false);
      this.closeMobile(false);
    }
    this.storedMenu.set(localStorage.getItem('menu'));
  }
  resetMenuToDefault(): void { localStorage.removeItem('menu'); this.cargarMenu(); }
  // Compatibility entry point: visual state must never replace the login menu.
  guardarMenu(): void { /* Expansion lives in this root service for the current session. */ }
  toggleSidebar(): void { this.collapsed.update(value => !value); }
  isExpanded(id: string): boolean { return this.expanded()[id] ?? false; }
  toggleSubmenu(group: NavigationGroup): void {
    if (this.miniSidebar && !this.mobileOpen()) { this.miniSidebar = false; this.expanded.update(value => ({ ...value, [group.id]: true })); return; }
    this.expanded.update(value => ({ ...value, [group.id]: !value[group.id] }));
  }
  revealActiveGroup(): void {
    const group = this.groups().find(group => group.submenu.some(item => this.isActive(item)));
    if (group) {
      if (!this.isExpanded(group.id)) this.expanded.update(value => ({ ...value, [group.id]: true }));
      if (group.id !== 'settings' && this.isExpanded('settings')) {
        this.expanded.update(value => ({ ...value, settings: false }));
      }
    } else if (this.isExpanded('settings')) {
      this.expanded.update(value => ({ ...value, settings: false }));
    }
  }
  isActive(item: NavigationItem): boolean {
    const path = this.currentUrl().split(/[?#]/)[0];
    if (path === item.url) return true;
    if (item.id === 'uco-clients') return /^\/uco\/(business\/|customer\/|prospects(?:\/|$)|agreements(?:\/|$)|sa-agreement\/|preinstall-survey\/)/.test(path);
    if (item.id === 'eco-clients') return path.startsWith('/eco/client/');
    if (item.id === 'uco-orders') return path.startsWith('/uco/manage-order/') || path === '/uco/create-order';
    if (item.id === 'uco-collection') return path.startsWith('/uco/route/');
    return !item.id.endsWith('dashboard') && path.startsWith(item.url + '/');
  }
  openMobile(opener?: HTMLElement): void { this.opener = opener || null; this.mobileOpen.set(true); }
  closeMobile(restoreFocus = true): void {
    const wasOpen = this.mobileOpen();
    this.mobileOpen.set(false);
    if (wasOpen && restoreFocus) {
      const target = this.opener?.isConnected ? this.opener : document.querySelector<HTMLElement>('[data-sidebar-opener]');
      // Wait for Angular to remove inert from the content containing the trigger.
      setTimeout(() => {
        if (!this.mobileOpen()) {
          const liveTarget = target?.isConnected ? target : document.querySelector<HTMLElement>('[data-sidebar-opener]');
          liveTarget?.focus();
        }
      });
    }
    this.opener = null;
  }
}
